/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
        constructor() {
            super();
            this.section = this.closest('.js-product');
            this.productForm = this.section.querySelector('.js-product-form-main');
            this.optionSelectors = this.querySelectorAll('.option-selector');
            this.data = this.getProductData();
            this.variant = this.getSelectedVariant();

            this.updateAvailability();
            this.updateAddToCartButton();
            this.addEventListener('change', this.handleVariantChange.bind(this));
        }

        /**
         * Handles 'change' events on the variant picker element.
         * @param {object} evt - Event object.
         */
        handleVariantChange(evt) {
            this.variant = null;

            // Get selected variant data (if variant exists).
            this.variant = this.getSelectedVariant();

            const selectedOptions = this.getSelectedOptions();


            const option1 = selectedOptions[0];
            const option2 = selectedOptions[1];

            if (!this.variant) {
                const validVariants = this.data.product.variants.filter(v =>
                    v.options[0] === option1 && v.available
                );

                const bestMatch =
                    validVariants.find(v => v.options[1] === option2) ||
                    validVariants[0];

                if (bestMatch) {
                    this.optionSelectors.forEach((selector, index) => {
                        const value = bestMatch.options[index];

                        const input = selector.querySelector(`input[value="${value}"]`);

                        if (input) {
                            input.checked = true;
                            input.dispatchEvent(new Event('change', { bubbles: true }));
                        }


                        const btn = selector.querySelector('.custom-select__btn span');
                        if (btn) {
                            const labelEl = selector.querySelector(
                                `input[value="${value}"] + label .js-value`
                            );

                            if (labelEl) {
                                btn.textContent = labelEl.textContent.trim();
                            } else {

                                btn.textContent = value;
                            }
                        }
                    });

                    this.variant = bestMatch;
                }
            }


            if (this.variant) {
                this.updateMedia();
                this.updateUrl(evt);
                this.updateVariantInput();
            }

            this.updateAddToCartButton();
            this.updateAvailability();
            this.updatePrice();
            this.updateWeight();
            this.updateBarcode();
            this.updateBackorderText();
            this.updatePickupAvailability();
            this.updateSku();
            VariantPicker.updateLabelText(evt);

            this.dispatchEvent(new CustomEvent('on:variant:change', {
                bubbles: true,
                detail: {
                    form: this.productForm,
                    variant: this.variant,
                    product: this.data.product
                }
            }));
        }

        /**
         * Updates the "Add to Cart" button label and disabled state.
         */
        updateAddToCartButton() {
            this.productForm = this.section.querySelector('.js-product-form-main');
            if (!this.productForm) return;

            this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
            const variantAvailable = this.variant && this.variant.available;
            const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

            this.addBtn.disabled = !variantAvailable;
            this.addBtn.textContent = variantAvailable
                ? this.addBtn.dataset.addToCartText
                : unavailableStr;
        }

        /**
         * Updates the availability status of an option.
         * @param {Element} optionEl - Option element.
         * @param {boolean} exists - Does this option lead to a variant that exists?
         * @param {boolean} available - Does this option lead to a variant that is available to buy?
         */
        static updateOptionAvailability(optionEl, exists, available) {
            const el = optionEl;
            const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
            el.classList.toggle('is-unavailable', !available);
            el.classList.toggle('is-nonexistent', !exists);

            if (optionEl.classList.contains('custom-select__option')) {
                const em = el.querySelector('em');

                if (em) {
                    em.hidden = available;
                }

                if (!available) {
                    if (em) {
                        em.textContent = unavailableText;
                    } else {
                        el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
                    }
                }
            } else {
                // The label may not be the input's next sibling (some products render
                // labels outside the flex container). Look up by `for=id` instead.
                const lbl =
                    (el.nextElementSibling && el.nextElementSibling.tagName === 'LABEL'
                        ? el.nextElementSibling
                        : null) ||
                    (el.id
                        ? document.querySelector('label[for="' + CSS.escape(el.id) + '"]')
                        : null);
                if (lbl) {
                    if (!available) lbl.title = unavailableText;
                    else lbl.removeAttribute('title');
                }
            }
        }

        /**
         * Updates the availability status in option selectors.
         */
        updateAvailability() {
            if (this.dataset.showAvailability === 'false') return;
            const { availabilityMethod } = this.dataset;
            let currVariant = this.variant;
            const selectedOptions = this.getSelectedOptions();

            if (!this.variant) {
                currVariant = { options: selectedOptions };
            }

            if (availabilityMethod === 'selection') {
                this.querySelectorAll('.js-option').forEach((optionEl) => {
                    VariantPicker.updateOptionAvailability(optionEl, false, false);
                });

                this.optionSelectors.forEach((selector, selectorIndex) => {
                    this.data.product.variants.forEach((variant) => {
                        let matchCount = 0;

                        variant.options.forEach((option, optionIndex) => {
                            if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                                matchCount += 1;
                            }
                        });

                        if (matchCount === currVariant.options.length - 1) {
                            const options = selector.querySelectorAll('.js-option');
                            const optionEl = Array.from(options).find((opt) => {
                                if (selector.dataset.selectorType === 'dropdown') {
                                    return opt.dataset.value === variant.options[selectorIndex];
                                }
                                return opt.value === variant.options[selectorIndex];
                            });

                            if (optionEl) {
                                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
                            }
                        }
                    });
                });
            } else {
                this.optionSelectors.forEach((selector, selectorIndex) => {
                    const options = selector.querySelectorAll('.js-option:not([data-value=""])');
                    options.forEach((option) => {
                        const optionValue =
                            selector.dataset.selectorType === 'dropdown'
                                ? option.dataset.value
                                : option.value;

                        let variantsExist = false;
                        let variantsAvailable = false;

                        this.data.product.variants.forEach((v) => {
                            let matches = 0;
                            for (let i = 0; i < selectorIndex; i += 1) {
                                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                                    matches += 1;
                                }
                            }
                            if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                                variantsExist = true;
                                if (v.available) {
                                    variantsAvailable = true;
                                }
                            }
                        });

                        VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
                    });
                });
            }
        }

        /**
         * Updates the backorder text and visibility.
         */
        updateBackorderText() {
            this.backorder = this.backorder || this.section.querySelector('.backorder');
            if (!this.backorder) return;

            let hideBackorder = true;

            if (this.variant && this.variant.available) {
                const { inventory } = this.data.formatted[this.variant.id];

                if (this.variant.inventory_management && inventory === 'none') {
                    const backorderProdEl = this.backorder.querySelector('.backorder__product');
                    const prodTitleEl = this.section.querySelector('.product-title');
                    const variantTitle = this.variant.title.includes('Default')
                        ? ''
                        : ` - ${this.variant.title}`;

                    backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
                    hideBackorder = false;
                }
            }

            this.backorder.hidden = hideBackorder;
        }

        /**
         * Updates the color option label text.
         */
        static updateLabelText(evt) {
            const selector = evt.target.closest('.option-selector');
            if (!selector || selector.dataset.selectorType === 'dropdown') return;

            const colorText = selector.querySelector('.js-color-text');
            if (!colorText) return;

            const labelEl = selector.querySelector(`label[for="${evt.target.id}"]`);
            const valueEl = labelEl && labelEl.querySelector('.js-value');
            if (valueEl) {
                colorText.textContent = valueEl.textContent;
            } else {
                colorText.textContent = evt.target.value;
            }
        }

        updateMedia() {
            if (!this.variant) return;

            const mediaId = this.variant.featured_media && this.variant.featured_media.id;
            const imageSrc =
                (this.variant.featured_image && this.variant.featured_image.src) ||
                (this.variant.featured_media &&
                    this.variant.featured_media.preview_image &&
                    this.variant.featured_media.preview_image.src);

            if (!mediaId && !imageSrc) return;

            if (this.section.matches('quick-add-drawer')) {
                if (mediaId) this.section.updateMedia(mediaId);
                return;
            }

            this.mediaGallery =
                this.mediaGallery || this.section.querySelector('media-gallery');
            if (!this.mediaGallery) return;

            let variantMedia = null;
            if (mediaId) {
                variantMedia = this.mediaGallery.querySelector(
                    `[data-media-id="${mediaId}"]`
                );
            }
            if (!variantMedia && imageSrc) {
                const imageKey = imageSrc.split('?')[0].split('/').pop();
                const items = this.mediaGallery.querySelectorAll('[data-media-id]');
                for (let i = 0; i < items.length; i += 1) {
                    const img = items[i].querySelector('img');
                    const itemSrc = img && (img.currentSrc || img.src || '');
                    if (itemSrc.split('?')[0].endsWith(imageKey)) {
                        variantMedia = items[i];
                        break;
                    }
                }
            }
            if (!variantMedia) return;
            this.mediaGallery.setActiveMedia(variantMedia, true, true);
        }

        updatePickupAvailability() {
            this.pickUpAvailability =
                this.pickUpAvailability || this.section.querySelector('pickup-availability');
            if (!this.pickUpAvailability) return;

            if (this.variant && this.variant.available) {
                this.pickUpAvailability.getAvailability(this.variant.id);
            } else {
                this.pickUpAvailability.removeAttribute('available');
                this.pickUpAvailability.innerHTML = '';
            }
        }

        updatePrice() {
            this.price =
                this.price || this.section.querySelector('.product-info__price > .price');
            if (!this.price) return;

            if (this.variant) {
                const priceCurrentEl = this.price.querySelector('.price__current');
                const priceWasEl = this.price.querySelector('.price__was');
                const unitPriceEl = this.price.querySelector('.unit-price');

                priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;

                if (priceWasEl)
                    priceWasEl.innerHTML =
                        this.data.formatted[this.variant.id].compareAtPrice || '';

                if (this.variant.unit_price_measurement) {
                    const valueEl = this.price.querySelector('.unit-price__price');
                    const unitEl = this.price.querySelector('.unit-price__unit');

                    const value = this.variant.unit_price_measurement.reference_value;
                    const unit = this.variant.unit_price_measurement.reference_unit;

                    valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
                    unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
                }

                unitPriceEl.hidden = !this.variant.unit_price_measurement;

                this.price.classList.toggle(
                    'price--on-sale',
                    this.variant.compare_at_price > this.variant.price
                );

                this.price.classList.toggle(
                    'price--sold-out',
                    !this.variant.available
                );
            }

            this.price.querySelector('.price__default').hidden = !this.variant;
            this.price.querySelector('.price__no-variant').hidden = this.variant;
        }

        updateWeight() {
            this.weights =
                this.weights || this.section.querySelectorAll('.product-info__weight');
            if (this.weights.length === 0) return;

            const weightAvailable = this.variant && this.variant.weight > 0;

            this.weights.forEach((weight) => {
                weight.textContent = weightAvailable
                    ? this.data.formatted[this.variant.id].weight
                    : '';
                weight.hidden = !weightAvailable;
            });
        }

        updateBarcode() {
            this.barcodes =
                this.barcodes ||
                this.section.querySelectorAll('.product-info__barcode-value');
            if (this.barcodes.length === 0) return;

            const barcodeAvailable = this.variant && this.variant.barcode;

            this.barcodes.forEach((barcode) => {
                barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
                barcode.parentNode.hidden = !barcodeAvailable;
            });
        }

        updateSku() {
            this.sku = this.sku || this.section.querySelector('.product-sku__value');
            if (!this.sku) return;

            const skuAvailable = this.variant && this.variant.sku;
            this.sku.textContent = skuAvailable ? this.variant.sku : '';
            this.sku.parentNode.hidden = !skuAvailable;
        }

        updateUrl(evt) {
            if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
            window.history.replaceState(
                {},
                '',
                `${this.dataset.url}?variant=${this.variant.id}`
            );
        }

        updateVariantInput() {
            this.forms =
                this.forms ||
                this.section.querySelectorAll(
                    '.js-product-form-main, .js-instalments-form'
                );

            this.forms.forEach((form) => {
                const input = form.querySelector('input[name="id"]');
                input.value = this.variant.id;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }

        getSelectedOptions() {
            const selectedOptions = [];

            this.optionSelectors.forEach((selector) => {
                if (selector.dataset.selectorType === 'dropdown') {
                    const btn = selector.querySelector('.custom-select__btn');
                    selectedOptions.push(btn ? btn.textContent.trim() : null);
                } else {
                    const checked = selector.querySelector('input:checked');
                    selectedOptions.push(checked ? checked.value : null);
                }
            });

            return selectedOptions;
        }

        getProductData() {
            const dataEl = this.querySelector('[type="application/json"]');
            return JSON.parse(dataEl.textContent);
        }

        getSelectedVariant() {
            const selectedOptions = this.getSelectedOptions();
            return this.data.product.variants.find((v) =>
                v.options.every((val, index) => val === selectedOptions[index])
            );
        }
    }

    customElements.define('variant-picker', VariantPicker);
}